import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PhoneCall, ArrowRight } from "@phosphor-icons/react";

const HERO_STYLE = {
    backgroundColor: "#141210",
    backgroundImage: `
        radial-gradient(circle at 80% 15%, rgba(255,90,54,0.24), transparent 45%),
        radial-gradient(circle at 10% 90%, rgba(255,90,54,0.10), transparent 40%),
        linear-gradient(rgba(255,255,255,0.045) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255,255,255,0.045) 1px, transparent 1px)
    `,
    backgroundSize: "100% 100%, 100% 100%, 46px 46px, 46px 46px",
};

export default function Login() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    if (user) return <Navigate to={user.role === "super_admin" ? "/platform" : "/"} replace />;

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setBusy(true);
        try {
            const u = await login(email, password);
            navigate(u?.role === "super_admin" ? "/platform" : "/");
        } catch (e) {
            setErr(e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
            {/* Left: dark textured hero */}
            <div className="relative hidden flex-col justify-between overflow-hidden p-12 lg:flex xl:p-16" style={HERO_STYLE} data-testid="auth-hero">
                <div className="grain pointer-events-none absolute inset-0 opacity-40" />
                <div className="relative flex items-center gap-2.5">
                    <img src="/inspiro-connect-mark.png" alt="Inspiro Connect" className="h-10 w-10 object-contain" />
                    <div>
                        <div className="font-heading text-lg font-bold text-white">Inspiro Connect</div>
                        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/50">Omnichannel, unified, and intelligence</div>
                    </div>
                </div>

                <div className="relative max-w-lg">
                    <h2 className="font-heading text-5xl font-extrabold leading-[1.03] tracking-tight text-white xl:text-6xl">
                        One dialer.<br />Any provider.<br /><span className="text-brand">Built for speed.</span>
                    </h2>
                    <p className="mt-6 max-w-md text-sm leading-relaxed text-white/60">
                        The browser-based softphone &amp; CTI for SMB sales teams — Twilio, Plivo and Exotel, provider-agnostic by design.
                    </p>
                    <div className="mt-9 flex flex-wrap gap-x-6 gap-y-2 text-[11px] font-bold uppercase tracking-[0.2em] text-white/45">
                        <span>Multi-provider</span>
                        <span>AI assistant</span>
                        <span>Salesforce-native</span>
                    </div>
                </div>

                <div className="relative" aria-hidden />
            </div>

            {/* Right: form */}
            <div className="flex flex-col justify-center bg-paper px-8 py-12 sm:px-16 lg:px-24">
                <div className="mx-auto w-full max-w-sm">
                    <div className="mb-10 flex lg:hidden">
                        <img src="/inspiro-connect-logo.png" alt="Inspiro Connect" className="h-9 w-auto object-contain" />
                    </div>

                    <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">Welcome back</div>
                    <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">Sign in</h1>
                    <p className="mt-2 text-sm text-stone2">Access your workspace.</p>

                    <form onSubmit={submit} className="mt-9 space-y-5" data-testid="login-form">
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-widest text-stone2">Email</label>
                            <input
                                data-testid="login-email-input"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="mt-2 w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-base outline-none transition-all placeholder:text-stone-400 focus:border-brand focus:ring-4 focus:ring-brand/15"
                                placeholder="you@company.com"
                            />
                        </div>
                        <div>
                            <div className="flex items-center justify-between">
                                <label className="text-[11px] font-bold uppercase tracking-widest text-stone2">Password</label>
                                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-semibold text-brand hover:underline">
                                    Forgot password?
                                </Link>
                            </div>
                            <input
                                data-testid="login-password-input"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="mt-2 w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-base outline-none transition-all placeholder:text-stone-400 focus:border-brand focus:ring-4 focus:ring-brand/15"
                                placeholder="••••••••"
                            />
                        </div>
                        {err && (
                            <div data-testid="login-error" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                                {err}
                            </div>
                        )}
                        <button
                            data-testid="login-submit-button"
                            type="submit"
                            disabled={busy}
                            className="group flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-3.5 font-heading text-base font-semibold text-white shadow-lg shadow-brand/25 transition-all hover:-translate-y-0.5 hover:bg-brand-hover disabled:opacity-50"
                        >
                            {busy ? "Signing in…" : "Sign In"}
                            <ArrowRight size={18} className="transition-transform group-hover:translate-x-0.5" />
                        </button>
                    </form>

                    <p className="mt-8 text-sm text-stone2">
                        New here?{" "}
                        <Link to="/register" data-testid="go-register-link" className="font-semibold text-ink underline-offset-4 hover:text-brand hover:underline">
                            Create your workspace →
                        </Link>
                    </p>

                    <nav className="mt-6 flex flex-wrap gap-x-4 gap-y-1 text-xs text-stone2">
                        <Link to="/privacy-policy" data-testid="login-privacy-link" className="transition-colors hover:text-brand">Privacy Policy</Link>
                        <span className="text-stone-300">·</span>
                        <Link to="/terms-of-service" data-testid="login-terms-link" className="transition-colors hover:text-brand">Terms of Service</Link>
                        <span className="text-stone-300">·</span>
                        <Link to="/data-deletion" data-testid="login-data-deletion-link" className="transition-colors hover:text-brand">Data Deletion</Link>
                    </nav>
                </div>
            </div>
        </div>
    );
}
